import type { ApiResult } from "@sbd-tcic/shared-types";

/**
 * In local dev, Vite's proxy (vite.config.ts) forwards a relative `/api/v1` path to the api
 * Worker on the same origin — no env var needed. Once deployed, the Pages SPA and the api Worker
 * live on different origins, so the build needs an absolute URL; `VITE_API_BASE_URL` supplies it
 * at build time (see apps/web/.env.production).
 */
const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v1`;

export class ApiClientError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await res.json()) as ApiResult<T>;
  if (!body.ok) {
    throw new ApiClientError(res.status, body.error.error, body.error.message, body.error.details);
  }
  return body.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, data?: unknown) => request<T>(path, { method: "POST", body: data !== undefined ? JSON.stringify(data) : undefined }),
  patch: <T>(path: string, data?: unknown) => request<T>(path, { method: "PATCH", body: data !== undefined ? JSON.stringify(data) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
